import { ClusterClass } from "@/gen_src/proto/chalk/server/v1/cluster_class_pb";
import {
  Environment,
  EnvironmentSuspensionSource,
} from "@/gen_src/proto/chalk/server/v1/environment_pb";
import { BillingCloud } from "@/gen_src/proto/chalk/usage/v1/rate_pb";
import { assertNever } from "@/utils/asserts";

export type CloudProviderType = "aws" | "gcp" | "azure" | "unknown";

export function isAdminSuspended(environment: Environment | null | undefined): boolean {
  return environment?.suspensionSource === EnvironmentSuspensionSource.ADMIN;
}

export function cloudProviderTypeFromEnvironmentPB(environment: Environment): CloudProviderType {
  const kind = environment.cloudConfig?.config.case;
  switch (kind) {
    case "aws":
      return "aws";
    case "gcp":
      return "gcp";
    case "azure":
      return "azure";
    default:
      return "unknown";
  }
}

export function cloudProviderTypeFromClusterKind(kind: string): CloudProviderType {
  if (kind.startsWith("EKS_")) return "aws";
  if (kind.startsWith("GKE_")) return "gcp";
  if (kind.startsWith("AKS_")) return "azure";
  return "unknown";
}

export function cloudUsesKarpenter(cloudProvider: CloudProviderType): boolean {
  return cloudProvider === "aws" || cloudProvider === "azure";
}

/**
 * Whether the environment's primary (kube) cluster is serverless. Mirrors the server-side
 * convention: only CLUSTER_CLASS_SERVERLESS is serverless; UNSPECIFIED/HOSTED (and environments not
 * linked to any cluster, which surface as UNSPECIFIED) are treated as hosted.
 */
export function isServerlessEnvironmentPB(environment: Environment): boolean {
  return environment.primaryLinkedClusterClass === ClusterClass.SERVERLESS;
}

// Whether the environment's cloud supports the simplified "chalk machine type" resource abstraction.
// Karpenter clouds (AWS/Azure) map a machine type to an instance type + nodepool; GCP maps it to a
// GKE custom ComputeClass. Unlike cloudUsesKarpenter, this is NOT a proxy for karpenter-specific
// behavior (e.g. instance-type resource inference) — use it only to gate machine-type UI.
export function cloudSupportsChalkMachineTypes(cloudProvider: CloudProviderType): boolean {
  return cloudProvider === "aws" || cloudProvider === "azure" || cloudProvider === "gcp";
}

export const cloudProviderToBillingCloud = (cloudProvider: CloudProviderType): BillingCloud => {
  switch (cloudProvider) {
    case "aws":
      return BillingCloud.AWS;
    case "gcp":
      return BillingCloud.GCP;
    case "azure":
      return BillingCloud.AZURE;
    case "unknown":
      return BillingCloud.UNSPECIFIED;
    default:
      assertNever(cloudProvider);
      return BillingCloud.UNSPECIFIED;
  }
};

// Human-readable cloud name, matching the casing used elsewhere in the product (AWS/GCP/Azure).
// `unknown` has no name worth showing, so callers phrase around it rather than printing a placeholder.
export const cloudProviderDisplay = (cloudProvider: CloudProviderType): string | undefined => {
  switch (cloudProvider) {
    case "aws":
      return "AWS";
    case "gcp":
      return "GCP";
    case "azure":
      return "Azure";
    case "unknown":
      return undefined;
    default:
      assertNever(cloudProvider);
      return undefined;
  }
};
